import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  declare props: Props;
  declare setState: any;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught React Error:', error, errorInfo);
    this.setState({ error, errorInfo });
    // V9.2.9: antes o erro só ia para o console — a tela "Recuperação do Player" aparecia e ninguém sabia onde
    // tinha quebrado. Agora vai para a Telemetria do Gerenciador e, no stick, para o log do aparelho, com a pilha.
    try {
      const msg = `${error?.name || 'Erro'}: ${error?.message || String(error)}`;
      const pilha = String(error?.stack || '').split('\n').slice(0, 6).join(' | ');
      const comp = String(errorInfo?.componentStack || '').trim().split('\n').slice(0, 6).map((s) => s.trim()).join(' < ');
      const texto = `TELA QUEBROU (Recuperação do Player): ${msg} | pilha: ${pilha} | componentes: ${comp}`.slice(0, 1500);
      try { (window as any).AndroidBridge?.logStick?.('erro', 'app', texto); } catch { /* fora do stick */ }
      import('../../utils/remoteLog')
        .then((m: any) => {
          try { m.remoteLog?.('react_crash', texto, 'error'); } catch { /* nada */ }
          try { m.flushRemoteLog?.(); } catch { /* nada */ }
        })
        .catch(() => {});
    } catch { /* nunca quebrar a tela de recuperação */ }
  }

  private handleResetApp = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {}
    window.location.href = window.location.origin + window.location.pathname;
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="w-screen h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center select-none font-sans z-50">
          <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-3xl p-8 shadow-2xl flex flex-col items-center">
            <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 mb-5">
              <AlertTriangle className="w-8 h-8" />
            </div>

            <h2 className="text-xl font-black text-white mb-2">Recuperação do Player</h2>
            <p className="text-xs text-zinc-400 mb-6 leading-relaxed">
              O aplicativo encontrou uma exceção inesperada e foi protegido automaticamente.
            </p>

            {this.state.error && (
              <div className="w-full bg-black/60 border border-zinc-800 rounded-xl p-3 mb-6 text-left max-h-32 overflow-y-auto">
                <p className="text-[11px] font-mono text-red-300 break-all">
                  {this.state.error.toString()}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <button
                onClick={this.handleReload}
                className="flex-1 px-4 py-3 rounded-xl bg-theme-accent hover:bg-theme-accent-hover text-black font-black text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-md"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Recarregar</span>
              </button>

              <button
                onClick={this.handleResetApp}
                className="px-4 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white font-bold text-xs flex items-center justify-center gap-2 border border-zinc-700 transition cursor-pointer"
                title="Limpa caches locais e reinicia"
              >
                <Trash2 className="w-4 h-4 text-red-400" />
                <span>Limpar Cache & Resetar</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
